import { DraftSectionItem } from '../../components/Section/Section.model'
import {
  DraftQuestionnaireEntry,
  EntryType
} from '../../data/model/questionnaire'
import { isHiddenIn } from '../../modules/Questionnaire/MarketVisibility/marketVisibility'

/**
 * Per-market export mirrors the respondent-side filter in cin-questionnaire
 * (`MarketVisibilityDomainV2.filterEntriesForMarket` +
 * `RouteHelpersV2.removeDanglingRoutesFromQuestionnaire`). The builders keep
 * the full master list for numbering and lookups and only skip what is hidden
 * at render time, so question numbers and answer codes stay the master's.
 *
 * With no `marketSurveyId` everything is visible.
 */
export const isItemHiddenInMarket = (
  hiddenInMarkets: string[] | null | undefined,
  marketSurveyId: string | undefined
): boolean => !!marketSurveyId && isHiddenIn(hiddenInMarkets, marketSurveyId)

/**
 * Sections have no visibility of their own in the editor: like the backend, a
 * section counts as visible when at least one of its children is.
 */
export const isEntryHiddenInMarket = (
  entry: DraftQuestionnaireEntry,
  marketSurveyId: string | undefined
): boolean => {
  if (!marketSurveyId) return false
  if (entry.entryType === EntryType.SectionEntryType) {
    // @todo `entries` can be missing on partially loaded data
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const children = (entry.entryItem as DraftSectionItem).entries ?? []
    return children.every((child) =>
      isEntryHiddenInMarket(child, marketSurveyId)
    )
  }
  return isHiddenIn(entry.hiddenInMarkets, marketSurveyId)
}

/**
 * The entry as a respondent in the market sees it: response options, matrix
 * columns and matrix rows hidden in the market removed. Only for consumers
 * that don't print answer codes (the behaviour summary); the content builders
 * skip hidden items in place so codes stay the master's.
 */
export const entryAsSeenInMarket = (
  entry: DraftQuestionnaireEntry,
  marketSurveyId: string | undefined
): DraftQuestionnaireEntry => {
  if (!marketSurveyId) return entry
  const visible = <T extends { hiddenInMarkets?: string[] | null }>(
    items: T[]
  ) => items.filter((item) => !isHiddenIn(item.hiddenInMarkets, marketSurveyId))

  switch (entry.entryType) {
    case EntryType.QuestionEntryType:
      return {
        ...entry,
        entryItem: {
          ...entry.entryItem,
          responseOptions: visible(entry.entryItem.responseOptions)
        }
      }
    case EntryType.MatrixEntryType:
      return {
        ...entry,
        entryItem: {
          ...entry.entryItem,
          responseOptions: visible(entry.entryItem.responseOptions),
          matrixRows: visible(entry.entryItem.matrixRows)
        }
      }
    default:
      return entry
  }
}
